import {ArrowRightIcon} from '@primer/octicons-react'
import {Banner} from '@primer/react'
import {useCallback} from 'react'

import {useNewTopicNudge} from '../hooks/use-new-topic-nudge'
import styles from './NewTopicNudge.module.css'

/**
 * Docs page explaining how conversation length affects Copilot usage and when to start a fresh chat.
 * Opened from the banner's "Learn more" action.
 */
const LEARN_MORE_URL = 'https://docs.github.com/en/copilot/tutorials/optimize-chat-usage'

interface NewTopicNudgeProps {
  /**
   * The composer textarea. When the banner is dismissed it unmounts and removes the focused dismiss
   * button, so focus is moved here (the natural next target) to avoid dropping focus to the body.
   */
  composerRef?: React.RefObject<HTMLTextAreaElement | null>
}

/**
 * A dismissible nudge shown above the composer when the user returns to a stale, long immersive
 * thread. It suggests the user start a fresh chat for a new topic to keep usage lower. Visibility,
 * dismissal, and the "start new chat" action are all owned by {@link useNewTopicNudge}; this
 * component only renders.
 *
 * Rendered as a non-blocking Primer info `Banner` (blue) attached to the top of the chat input so it
 * never covers conversation content or shifts the message list. Single horizontal row: info icon +
 * message, with the secondary "Learn more" action on the left and the primary "Start new chat"
 * action on the right, followed by the dismiss button.
 *
 * The banner is announced politely to assistive technology (`aria-live`/`aria-atomic`) since it is
 * inserted dynamically, and dismissing it moves focus to the composer so keyboard/SR focus is not
 * dropped when the banner unmounts.
 */
export function NewTopicNudge({composerRef}: NewTopicNudgeProps) {
  const {show, dismiss, startNewChat, explain} = useNewTopicNudge()

  const handleDismiss = useCallback(() => {
    dismiss()
    // The banner (and its focused dismiss button) unmounts on dismiss; move focus to the composer
    // so it lands somewhere meaningful instead of falling back to <body>.
    composerRef?.current?.focus()
  }, [dismiss, composerRef])

  if (!show) return null

  return (
    <div className={styles.root}>
      <Banner
        className={styles.banner}
        variant="info"
        actionsLayout="inline"
        aria-label="Start a new chat for a new topic"
        aria-live="polite"
        aria-atomic="true"
        title="New topic?"
        hideTitle
        description="New topic? A fresh chat keeps your usage lower."
        onDismiss={handleDismiss}
        secondaryAction={
          <Banner.SecondaryAction
            as="a"
            href={LEARN_MORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={explain}
          >
            Learn more
          </Banner.SecondaryAction>
        }
        primaryAction={
          <Banner.PrimaryAction trailingVisual={ArrowRightIcon} onClick={startNewChat}>
            Start new chat
          </Banner.PrimaryAction>
        }
      />
    </div>
  )
}
